import { parseMoney } from './fixedAssetsFormat'

export type ScheduleMonthInput = {
  year: number
  month: number
  expense: string
  expected_accum: string
  nbv: string
}

export type ChartPoint = {
  key: string
  xIndex: number
  nbv: number
  expense: number
  accum: number
  isProjected: boolean
}

function monthKey(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`
}

export function currentMonthKey(date: Date): string {
  return monthKey(date.getFullYear(), date.getMonth() + 1)
}

export function buildChartPoints(params: {
  purchaseDate: string
  purchasePrice: string | number
  salvage: string | number
  months: ScheduleMonthInput[]
  today?: Date
}): ChartPoint[] {
  const { purchaseDate, purchasePrice, months, today = new Date() } = params

  if (months.length === 0) {
    return []
  }

  const price = typeof purchasePrice === 'number' ? purchasePrice : parseMoney(purchasePrice)
  const purchaseMonthKey = purchaseDate.slice(0, 7)
  const todayKey = currentMonthKey(today)

  const schedulePoints: ChartPoint[] = months
    .map((row) => {
      const key = monthKey(row.year, row.month)
      return {
        key,
        xIndex: 0,
        nbv: parseMoney(row.nbv),
        expense: parseMoney(row.expense),
        accum: parseMoney(row.expected_accum),
        isProjected: key > todayKey,
      }
    })
    .sort((a, b) => a.key.localeCompare(b.key))

  let points: ChartPoint[]
  if (purchaseMonthKey < schedulePoints[0]!.key) {
    points = [
      {
        key: purchaseMonthKey,
        xIndex: 0,
        nbv: price,
        expense: 0,
        accum: 0,
        isProjected: purchaseMonthKey > todayKey,
      },
      ...schedulePoints,
    ]
  } else {
    points = schedulePoints
  }

  points.forEach((point, index) => {
    point.xIndex = index
  })

  return points
}

export function chartXScale(xIndex: number, pointCount: number, innerWidth: number): number {
  if (pointCount <= 1) return 0
  return (xIndex / (pointCount - 1)) * innerWidth
}

export function chartYScale(value: number, yMax: number, innerHeight: number): number {
  if (yMax <= 0) return innerHeight
  return innerHeight - (value / yMax) * innerHeight
}

export function nbvLinePath(
  points: ChartPoint[],
  innerWidth: number,
  innerHeight: number,
  yMax: number,
  pointCount = points.length,
): string {
  if (points.length === 0) return ''

  return points
    .map((point, index) => {
      const x = chartXScale(point.xIndex, pointCount, innerWidth)
      const y = chartYScale(point.nbv, yMax, innerHeight)
      return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`
    })
    .join(' ')
}

export function todayX(points: ChartPoint[], today: Date, innerWidth: number): number | null {
  if (points.length === 0) return null

  const todayKey = currentMonthKey(today)
  const firstKey = points[0]!.key
  const lastKey = points[points.length - 1]!.key
  if (todayKey < firstKey || todayKey > lastKey) return null

  let index = points.findIndex((point) => point.key === todayKey)
  if (index === -1) {
    index = points.findIndex((point) => point.key >= todayKey)
    if (index === -1) index = points.length - 1
  }

  return chartXScale(points[index]!.xIndex, points.length, innerWidth)
}

export function accumAreaPath(
  points: ChartPoint[],
  purchasePrice: number,
  innerWidth: number,
  innerHeight: number,
  yMax: number,
): string {
  if (points.length === 0) return ''

  const n = points.length
  const topLine = points
    .map((point, index) => {
      const x = chartXScale(point.xIndex, n, innerWidth)
      const y = chartYScale(point.nbv, yMax, innerHeight)
      return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`
    })
    .join(' ')

  const lastX = chartXScale(points[n - 1]!.xIndex, n, innerWidth)
  const firstX = chartXScale(points[0]!.xIndex, n, innerWidth)
  const purchaseY = chartYScale(purchasePrice, yMax, innerHeight)

  return `${topLine} L ${lastX} ${purchaseY} L ${firstX} ${purchaseY} Z`
}

export function nearestPointIndex(
  points: ChartPoint[],
  innerWidth: number,
  mouseX: number,
): number {
  if (points.length === 0) return -1
  if (points.length === 1) return 0

  const n = points.length
  let nearest = 0
  let nearestDist = Infinity
  for (let i = 0; i < n; i++) {
    const x = chartXScale(points[i]!.xIndex, n, innerWidth)
    const dist = Math.abs(x - mouseX)
    if (dist < nearestDist) {
      nearestDist = dist
      nearest = i
    }
  }
  return nearest
}
